const DEFAULT_HTTP_ORIGIN = "http://localhost";
const TOKEN_SUBPROTOCOL_PREFIX = "token.";
const WEBSOCKET_SUBPROTOCOL_RE = /^[!#$%&'*+\-.^_`|~0-9A-Za-z]+$/;

const toWebSocketOrigin = (value: string) => value.replace(/^http/i, "ws");

const resolveWebSocketOrigin = (base?: string): string => {
  const candidate = base?.trim();
  const windowOrigin =
    typeof window !== "undefined"
      ? window.location.origin
      : DEFAULT_HTTP_ORIGIN;

  if (!candidate || candidate.startsWith("/")) {
    return toWebSocketOrigin(windowOrigin);
  }

  try {
    return toWebSocketOrigin(new URL(candidate, windowOrigin).origin);
  } catch {
    return toWebSocketOrigin(windowOrigin);
  }
};

export interface AuthenticatedWebSocketConfig {
  url: string;
  protocols: string[];
}

const socketsWaitingToClose = new WeakSet<WebSocket>();

export const closeWebSocketWhenReady = (socket: WebSocket): void => {
  if (socket.readyState === WebSocket.OPEN) {
    socket.close();
    return;
  }

  if (
    socket.readyState !== WebSocket.CONNECTING ||
    socketsWaitingToClose.has(socket)
  ) {
    return;
  }

  socketsWaitingToClose.add(socket);

  const cleanup = () => {
    socket.removeEventListener("open", closeAfterOpen);
    socket.removeEventListener("error", cleanup);
    socket.removeEventListener("close", cleanup);
    socketsWaitingToClose.delete(socket);
  };
  const closeAfterOpen = () => {
    cleanup();
    if (socket.readyState === WebSocket.OPEN) {
      socket.close();
    }
  };

  socket.addEventListener("open", closeAfterOpen, { once: true });
  socket.addEventListener("error", cleanup, { once: true });
  socket.addEventListener("close", cleanup, { once: true });
};

export const buildAuthenticatedWebSocketConfig = (
  path: string,
  token: string,
  base?: string
): AuthenticatedWebSocketConfig => {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const url = new URL(normalizedPath, `${resolveWebSocketOrigin(base)}/`);
  const tokenProtocol = `${TOKEN_SUBPROTOCOL_PREFIX}${token}`;

  if (!WEBSOCKET_SUBPROTOCOL_RE.test(tokenProtocol)) {
    throw new Error("Invalid websocket auth token");
  }

  return { url: url.toString(), protocols: [tokenProtocol] };
};
